// in both locations ./web-landing/src/constants/teamStreams.js & ./functions/constants/teamStreams.js
// -------------------------------------------------------------
// TEAM STREAMS (squad streaming) - SHARED CONSTANTS + SPLIT MATH
//
// Pooled squad streaming, v2 TEAM-BACKED (founder 2026-08-04): a squad IS
// the user's registered Team (teams/{teamId}) - the roster is exactly
// teams.memberIds and is managed ONLY from the Your Team tab. Viewers watch
// one squad page (multi-player grid + one shared chat), and a Super Crit
// sent on the squad page is split EQUALLY among the live+linked+verified
// members. Each member's share lands as a revenueLedger row, and (evergreen
// 3.2, from settings/creatorComp.directTipsSince) tips credit each verified
// member's earnings when the charge succeeds, payable after a settlement
// period of at least 48 hours - the same lane as personal Super Crits. The
// monthly creator-comp run pays only charges recorded before the cutover.
// Feature ships DARK behind settings/streams.teamStreamsEnabled (admins
// exempt).
//
// FOUNDER-TWEAKABLE LEVERS (change values here, logic reads them):
//   maxMembers          8  - display cap on roster members shown/split on a
//                            squad page (teams can be larger; the split set
//                            is live+linked+verified members, which this
//                            caps)
//   minMembers          2  - a team needs at least this many roster members
//                            before its squad can receive team Super Crits
//   maxNameLen         40  - squad display-name length cap (squad name = the
//                            team's name, truncated to this)
//
// SPLIT RULE (founder 2026-07-21, kept in v2; VERIFIED added in evergreen
// 3.2): a squad Super Crit splits equally among the roster members who are
// LIVE at send time AND LINKED to the squad AND VERIFIED (ID + age check,
// the only earn gate) - offline/unlinked/unverified members are excluded
// from that particular Super Crit. The squad "link" must also be ON
// (teamStreams.linkActive; the team captain toggles it) for squad Super
// Crits to be sendable at all.
//
// ACCOUNT OPT-IN (founder 2026-07-21, kept in v2): starting squad streaming
// for your team and LINKING yourself into the split require the user to
// have turned on squad streaming in their account first -
// users/{uid}[SQUAD_OPT_IN_FIELD] === true. Default is OFF. Being on a team
// roster alone never puts anyone in a revenue split. The field name is
// shared here so the frontend toggle and the backend enforcement can never
// drift.
//
// MODERATOR SET (v2): squad chat moderators = the TEAM CAPTAIN + admins
// only (not all members - keeps the mod-abuse surface small). See
// streamDirectory assertChatModerator.
// -------------------------------------------------------------

export const TEAM_STREAM_CONFIG = {
  maxMembers: 8,
  minMembers: 2,
  maxNameLen: 40,
};

// users/{uid} field for the account-level squad-streaming opt-in. Client
// writes it from the squad manage UI (allowed by the users-rules denylist -
// it is not a financial/verification field); the backend enforces it in
// modules/teamStreams.js on create/invite/accept.
export const SQUAD_OPT_IN_FIELD = 'squadStreamsOptIn';

/**
 * Deterministic equal split of a team Super Crit across squad members.
 *
 * Rules:
 *   - uids are deduped and sorted ascending, so the same inputs ALWAYS
 *     produce the same rows (webhook retries, UI previews, and the ledger
 *     all agree).
 *   - base = floor(amountCents / n); remainder r = amountCents - base * n;
 *     the first r uids (in sorted order) get +1 cent.
 *   - sum(shareCents) === amountCents EXACTLY - never a lost or minted cent.
 *   - chargeWeight = 1/n rides on every row so the comp engine's month-end
 *     per-charge 30-cent Stripe fixed-fee estimate sums to exactly ONE
 *     charge across the squad instead of N (see creatorComp
 *     splitRevenueForMonth).
 *
 * The minimum Super Crit tier is $1 (100 cents) and maxMembers is 8, so
 * every share is always >= 1 cent.
 *
 * Pure function, no imports - byte-identical in functions/ and web-landing/
 * so the frontend split preview matches the ledger to the cent.
 *
 * @param {Object} args
 * @param {number} args.amountCents  full Super Crit amount in cents (> 0)
 * @param {string[]} args.memberUids accepted member uids (deduped here)
 * @returns {Array<{ uid: string, shareCents: number, chargeWeight: number }>}
 */
export function computeSquadSplit({ amountCents, memberUids } = {}) {
  const amount = Math.max(0, Math.floor(Number(amountCents) || 0));
  const seen = {};
  const uids = [];
  if (Array.isArray(memberUids)) {
    for (const u of memberUids) {
      if (typeof u === 'string' && u && !seen[u]) {
        seen[u] = true;
        uids.push(u);
      }
    }
  }
  if (amount <= 0 || uids.length === 0) return [];

  uids.sort();
  const n = uids.length;
  const base = Math.floor(amount / n);
  const remainder = amount - base * n;
  const chargeWeight = 1 / n;

  return uids.map((uid, i) => ({
    uid,
    shareCents: base + (i < remainder ? 1 : 0),
    chargeWeight,
  }));
}
